import { Box, ButtonBase, Divider, TooltipProps } from '@mui/material';
import { colors, IconButton, Tooltip } from '@sweep-io/sweep-design';
import { useDispatch, useSelector } from 'react-redux';
import { SparklingWandColorIcon } from '../../../../../icons/generalIcons';
import * as Icons from '@sweep-io/sweep-design/dist/icons';
import React, { useCallback } from 'react';
import { selectIsFieldsDialogOpen } from '../../../../../reducers/funnel-flow-page/fieldsReducer';
import { usePanels } from '../../../../panels/usePanels';
import { selectAutomations } from '../../../../../reducers/global/automationReducers';
import { selectAlerts } from '../../../../../reducers/global/alertsReducers';
import { documentationPanelType } from '../../../../documentation/types';
import { automationsPanelType, playbookAlertsPanelType } from '../../../../Automations/panelTypes';
import { CrmOrgUsageNotification } from '../../../funnel-map-flow/left-menu/CrmOrgUsageNotification';
import { useFunnelMapPage } from '../../../funnel-map-flow/useFunnelMapPage';
import { SettingsButton } from '../../../funnel-map-flow/dialogs/funnel-settings-dialog/CanvasUnitedSettingsButton';
import { useConfigurationCanvas } from '../../../configuration-canvas/public/useConfigurationCanvas';
import { useConfigurationCanvasContext } from '../../../configuration-canvas/ConfigurationCanvasContext';
import { enableLayerFor, DEFAULT_LAYERS_ID } from '../../../../../reducers/canvasLayersReducer';
import { VisibilityLayers } from '../../../../../types/enums/VisibilityLayers';
import { useHandleCommentsAndAiFirst } from './useHandleCommentsAndAiFirst';
import { FunnelMapNameEdit } from '../../../funnel-map-flow/left-menu/FunnelMapNameEdit';
import useSendBiEvent from '../../../../../hooks/useSendBiEvent';
import { UI_EVENTS } from '../../../../../services/events';
import { AddFunnelMenu } from './AddFunnelMenu';
import useAgentsHome from '../../../../aiAgentCenter/useAgentsHome';
import useUnitedCanvasMediaQueries from '../useUnitedCanvasMediaQueries';
import { useProcessMining } from '../useProcessMining';
import useSweepNavigate from '../../../../../navigation-utils/useSweepNavigate';
import { appRoutes } from '../../../../../constants/appRoutes';
import { useFeatureToggle } from '../../../../../feature-flags/useFeatureToggle';

const TooltipWithDisabledTitle = React.forwardRef(
  (props: TooltipProps & { disabledTitle: string; showDisabledTitle?: boolean }, ref) => {
    const { title, showDisabledTitle, disabledTitle, ...otherProps } = props;
    const tooltipTitle = showDisabledTitle ? disabledTitle : title;
    return <Tooltip title={tooltipTitle} {...otherProps} ref={ref} />;
  },
);

export const CanvasUnitedLeftMenu = () => {
  const dispatch = useDispatch();
  const { canvasFunnelMap, canvasCrmOrgId } = useConfigurationCanvas();
  const { setCommentsOn, commentsOn } = useConfigurationCanvasContext();
  const { isPanelOpen } = usePanels();
  const sendBiEvent = useSendBiEvent();
  const { isCreateAgentActive } = useAgentsHome();
  const { isLoading: isProcessMiningLoading } = useProcessMining(canvasFunnelMap.id || '');
  const { canvasToolbar } = useFeatureToggle();

  const isAlertsPressed = !commentsOn && isPanelOpen(playbookAlertsPanelType);
  const isAutomationsPressed = !commentsOn && isPanelOpen(automationsPanelType);

  const isFieldsDialogOpen = useSelector(selectIsFieldsDialogOpen);
  const isDocumentationPressed = !commentsOn && isPanelOpen(documentationPanelType);
  const automationsList = useSelector(selectAutomations);
  const alertsList = useSelector(selectAlerts);
  const isFieldsPressed = isFieldsDialogOpen && !commentsOn;

  const toggleComments = useCallback(() => {
    const _commentsOn = !commentsOn;
    setCommentsOn(_commentsOn);
    if (_commentsOn) {
      sendBiEvent({ name: UI_EVENTS.canvasCommentMode });
      dispatch(enableLayerFor({ layersId: DEFAULT_LAYERS_ID, layer: VisibilityLayers.COMMENTS }));
    }
  }, [commentsOn, dispatch, sendBiEvent, setCommentsOn]);

  const {
    onAutomationsClick,
    onAlertsClick,
    onFieldsClick,
    onDocumentationClick,
    isFunnelMapEmpty,
  } = useFunnelMapPage();

  const handleCommentsAndAiFirst = useHandleCommentsAndAiFirst();

  const { isMediumScreen } = useUnitedCanvasMediaQueries();

  const gap = '16px';

  const disableButtons = isFunnelMapEmpty || isCreateAgentActive || isProcessMiningLoading;
  const { navigate } = useSweepNavigate();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap,
          padding: `0 ${gap}`,
          backgroundColor: colors.white,
          borderRadius: '6px',
          position: 'relative',
        }}
        data-testid="funnel-flow-left-menu"
      >
        <Tooltip title="Go back">
          <ButtonBase
            onClick={() => navigate(appRoutes.funnelMapsPage.route)}
            sx={{
              padding: '14px',
              margin: '-14px',
              ':hover svg .hover-color': {
                fill: colors.blue[500],
              },
            }}
          >
            <SparklingWandColorIcon />
          </ButtonBase>
        </Tooltip>

        <Divider orientation="vertical" variant="middle" flexItem />
        <FunnelMapNameEdit />
        {isMediumScreen && (
          <SettingsButton
            funnelMap={canvasFunnelMap}
            crmOrgId={canvasCrmOrgId}
            disabled={isCreateAgentActive || isProcessMiningLoading}
          />
        )}
        <Tooltip title="Documentation">
          <span>
            <IconButton
              variant="flat"
              onClick={() => handleCommentsAndAiFirst(onDocumentationClick)}
              pressed={isDocumentationPressed}
              disabled={isProcessMiningLoading}
            >
              <Icons.Binoculars />
            </IconButton>
          </span>
        </Tooltip>
        <TooltipWithDisabledTitle
          title="Automations"
          disabledTitle={isProcessMiningLoading ? '' : 'Please add a funnel to enable Automations'}
          showDisabledTitle={isFunnelMapEmpty}
        >
          <span>
            <IconButton
              variant="flat"
              badgeLabel={automationsList?.length}
              onClick={() => handleCommentsAndAiFirst(onAutomationsClick)}
              pressed={isAutomationsPressed}
              disabled={disableButtons}
            >
              <Icons.Automations />
            </IconButton>
          </span>
        </TooltipWithDisabledTitle>
        <TooltipWithDisabledTitle
          title="Alerts"
          disabledTitle={isProcessMiningLoading ? '' : 'Please add a funnel to enable Alerts'}
          showDisabledTitle={isFunnelMapEmpty}
        >
          <span>
            <IconButton
              variant="flat"
              badgeLabel={alertsList?.length}
              onClick={() => handleCommentsAndAiFirst(onAlertsClick)}
              pressed={isAlertsPressed}
              disabled={disableButtons}
            >
              <Icons.PlaybookAlert />
            </IconButton>
          </span>
        </TooltipWithDisabledTitle>

        <TooltipWithDisabledTitle
          title="Fields Management"
          disabledTitle={
            isProcessMiningLoading ? '' : 'Please add a funnel to enable Fields Management'
          }
          showDisabledTitle={isFunnelMapEmpty}
        >
          <span>
            <IconButton
              variant="flat"
              onClick={() => handleCommentsAndAiFirst(onFieldsClick)}
              pressed={!!isFieldsPressed}
              disabled={disableButtons}
            >
              <Icons.Fields />
            </IconButton>
          </span>
        </TooltipWithDisabledTitle>

        {isMediumScreen && !canvasToolbar && (
          <Tooltip title="Comments">
            <span>
              <IconButton
                variant="flat"
                onClick={toggleComments}
                badgeVariant="dot"
                pressed={commentsOn}
                disabled={isCreateAgentActive || isProcessMiningLoading}
              >
                <Icons.UnreadComment />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {!canvasToolbar && (
          <AddFunnelMenu disabled={isCreateAgentActive || isProcessMiningLoading} />
        )}
      </Box>

      <CrmOrgUsageNotification crmOrgId={canvasCrmOrgId} />
    </>
  );
};
